// client/src/pages/usecases/BrandingFeedbackPage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../index.css';
import '../../styles/HomePage.css';
import './usecases.css';
import { useAuth } from '../../authentication/AuthContext';
import { event } from '../../services/analyticsService';

function BrandingFeedbackPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view
    event({
      action: 'view_use_case_detail',
      category: 'engagement',
      label: 'branding_feedback'
    });
  }, []);

  const handleTryTemplate = () => {
    // Track template usage
    event({
      action: 'try_template',
      category: 'conversion',
      label: 'branding_feedback_template'
    });
    
    // Check if user is authenticated
    if (!user) {
      navigate('/login', { 
        state: { 
          from: '/research',
          message: 'Please log in to access the app and try this template'
        } 
      });
      return;
    }
    
    navigate('/research', { 
      state: { template: 'branding-feedback' } 
    });
  };

  const benefits = [
    {
      title: "Measure Brand Perception",
      description: "Understand the emotional responses and associations your brand evokes",
      alt: "Brand perception"
    },
    {
      title: "Compare Alternatives",
      description: "Test multiple brand concepts against each other to find the strongest option",
      alt: "Competitive comparison"
    },
    {
      title: "Demographic Insights",
      description: "See how different audience segments respond to your branding",
      alt: "Demographic insights"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Describe Your Brand Assets",
      description: "Input your brand name, tagline, values, visual identity elements, or messaging you want to test."
    },
    {
      number: 2,
      title: "Target Your Audience",
      description: "Select demographics that match your target market or explore different audience segments."
    },
    {
      number: 3,
      title: "Gather Brand Impressions",
      description: "Our AI personas provide authentic reactions to your brand, including emotional responses and associations."
    },
    {
      number: 4,
      title: "Analyze Brand Perception",
      description: "Review detailed feedback on brand clarity, appeal, memorability, and emotional connections."
    }
  ];

  // Define case study findings data
  const findings = [
    "The new logo scored <strong>40% higher</strong> on modernity and sustainability perceptions",
    "Their tagline <span class='highlight'><em>'Earth-friendly living, beautifully designed'</em></span> resonated strongly with women ages 28-45",
    "The color palette evoked the intended emotions of \"calm\" and \"natural\"",
    "Certain visuals unintentionally created luxury associations that didn't align with their accessible pricing strategy"
  ];

  return (
    <div className="pricing-page">
      <div className="top-hero">
        <div className="hero-grid-item">
          <h4 style={{fontWeight: '400'}}>USE CASE</h4>
          <h1 style={{fontSize: '4rem', marginBottom: "3vh", marginTop: "3vh"}}>Branding Feedback</h1>
          <h3 style={{fontWeight: '400'}}>Understand how your brand resonates</h3>
          
          <div className="button-container">
            <button onClick={handleTryTemplate} className="standout-button button">Try This Template</button>
          </div>
        </div>
      </div>

      <div className="hero" style={{backgroundColor: 'var(--secondary-color', padding: 0, marginTop: '5vh'}}> 
        <div className="content-grid-2" style={{margin: '6vh 10vw', gap: '7rem', justifyContent: 'center'}}>
          <div>
            <h2 style={{fontSize: '2.5rem', fontWeight: '500', position: 'relative', paddingLeft: '2rem'}}>
              <span style={{
                position: 'absolute',
                left: '-1rem',
                top: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
              <i>Your brand is more than a logo, it's how people feel about your company.</i>
              <span style={{
                position: 'absolute',
                right: '-1rem',
                bottom: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
            </h2>
          </div>
          <div style={{textAlign: 'center', }}>
            <p style={{lineHeight: '1.7', letterSpacing: '0.05em', fontWeight: '500', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', borderRadius: '10px', padding: '3vh 2vw', backgroundColor: 'var(--background-color)'}}>
              Discover how your audience <span style={{color: 'var(--tertiary-color)'}}>truly perceives</span> your brand!
              <br/> <br/>
              Instant Flows reveals insights about your identity, messaging, and visuals. Helping you 
              forge stronger connections and turn customers into loyal brand advocates.
            </p>
          </div>
        </div>
      </div>

      <div className="benefits-section">
        <h2 className="benefits-title">Key Benefits</h2>
        <div className="benefits-container">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-header">
                <div className="icon-container">
                  <img src="/InstantFlowsicon_wb.png" alt={benefit.alt} className="benefit-icon" />
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
              </div>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="hero hero-3">
        <h2>How It Works</h2>
        <div className="content-grid-4" style={{maxWidth: "1200px", margin: "0 4rem", gap: "1.5rem", }}>
          {steps.map((step, index) => (
            <div key={index} className="card" style={{padding: "1.5rem", transition: "transform 0.3s ease", marginBottom: '1rem'}}>
              <div style={{display: "flex", alignItems: "center", marginBottom: "0.5rem"}}>
                <div style={{
                  background: "var(--tertiary-color)", 
                  color: "white", 
                  width: "32px", 
                  height: "32px", 
                  borderRadius: "50%", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  fontWeight: "bold",
                  marginRight: "0.8rem"
                }}>{step.number}</div>
                <h3 style={{margin: "0", fontWeight: '500'}}>{step.title}</h3>
              </div>
              <p style={{margin: "0", textAlign: 'left'}}>{step.description}</p>
            </div>
          ))}
        </div>
        <div className="button-container" style={{maxHeight: "none", paddingBottom: "1rem"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this Template</button>
        </div>
      </div>

      <div className="hero hero-1" style={{backgroundColor: 'var(--secondary-color)', padding: "1rem", paddingBottom:'2rem', margin: '2rem auto', marginBottom: '0'}}>
        <h2 className="section-heading">Case Study</h2>
        <div className="case-study-container">
          <div className="section-content" style={{boxShadow: 'none'}}>
            <h3 className="case-study-title">EcoBloom Rebrand</h3>
            <p className="case-study-text">
              EcoBloom, a sustainable home goods company, used InstantFlows to test their rebrand before launch.
              <br/><br/>
              Their findings:
            </p>
            <div className="findings-list">
              {findings.map((finding, index) => (
                <p key={index} className="finding-item">
                  <span className="bullet-point">•</span>
                  <span dangerouslySetInnerHTML={{ __html: finding }}></span>
                </p>
              ))}
            </div>
            <p className="case-study-conclusion">
              These insights helped EcoBloom refine their brand identity to better align with their target market's expectations.
            </p>
          </div>
        </div>
      </div>

      <div className="hero hero-2" style={{backgroundColor: 'var(--tertiary-color)', paddingTop: '4rem'}}>
        <div className="textbox" style={{width: "70%", background: "transparent", boxShadow: "none", color: "white"}}>
          <blockquote style={{fontSize: "1.2rem", fontStyle: "italic", lineHeight: '2.2rem'}}>
            "We were torn between two brand directions for months. InstantFlows gave us clear data on which resonated better with our audience, and more importantly—why. The demographic breakdown showed us that our younger audience had completely different associations than we anticipated."
          </blockquote>
          <div style={{display: "flex", justifyContent: "right", alignItems: "center", marginTop: "2rem"}}>
            <div style={{textAlign: "right", marginLeft: "1rem"}}>
              <p style={{margin: 0, fontWeight: "600"}}>Marcus Chen</p>
              <p style={{margin: 0, fontSize: "0.9rem"}}>Creative Director, EcoBloom</p>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <h2 className="cta-title">Ready to Test Your Brand Perception?</h2>
        <p className="cta-description">Get insights on how your brand resonates with your target audience.</p>
        <div className="button-container" style={{maxHeight: "none"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this Template</button>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="examples-section">
        <h2 className="examples-title" style={{marginTop: '0'}}>See it in action</h2>
        <div className="examples-grid">
          <div 
            className="example-card" 
            onClick={() => navigate('/exampleresearches', { state: { filter: 'branding-feedback', example: 'example1' } })}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Brand Perception Analysis" 
            />
            <h3 className="example-title">Brand Perception Analysis</h3>
            <p className="example-description">
              Discover how a lifestyle brand uncovered unexpected emotional connections and refined their visual identity to better resonate with their audience.
            </p>
            <button className="button example-button">View Case Study</button>
          </div>
          
          <div 
            className="example-card" 
            onClick={() => navigate('/exampleresearches', { state: { filter: 'branding-feedback', example: 'example2' } })}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Brand Messaging Optimization" 
            />
            <h3 className="example-title">Brand Messaging Optimization</h3>
            <p className="example-description">
              See how a B2B company tested multiple taglines and value propositions to identify messaging that truly connected with their target decision-makers.
            </p>
            <button className="button example-button">View Case Study</button>
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default BrandingFeedbackPage;